import React, { useState, useRef, useEffect, useCallback } from "react";
import { X, Check, Loader2 } from "lucide-react";
import { color, motion } from "framer-motion";
import logo from "../../../assets/images/logo.png";
import toast from "react-hot-toast";
import { PROPACKS_IVR_GRID_OPTIONS, getCompleteProductList, icd10Codes } from "../../../data/conservativeCareDashboard";

// ============================================================================
// INITIAL STATE
// ============================================================================

const getInitialState = (data, provider) => ({
  // Patient Info (pre-filled)
  patientName: data ? `${data.first_name} ${data.last_name}` : '',
  dob: data?.date_of_birth || '',
  gender: data?.gender || 'Male',
  address: data?.address || '',
  city: data?.city || '',
  state: data?.state || '',
  zip: data?.zip_code || '',
  
  // Primary Insurance (pre-filled)
  primaryInsurance: data?.primary_insurance || '',
  primaryPolicyNumber: data?.primary_insurance_number || '',
  primaryPayerPhone: '',
  
  // Secondary Insurance (pre-filled)
  secondaryInsurance: data?.secondary_insurance || '',
  secondaryPolicyNumber: data?.secondary_insurance_number || '',
  secondaryPayerPhone: '',
  
  // Practice Info (pre-filled from provider)
  practiceName: provider?.facility || '',
  npi: provider?.npi_number || '',
  taxId: '',
  ptanId: '',
  practiceAddress: provider?.address || '',
  practiceCity: provider?.city || '',
  practiceState: provider?.state || '',
  practiceZip: provider?.zip_code || '',
  contactName: provider?.full_name || '',
  contactEmail: provider?.email || '',
  phone: provider?.phone_number || '',
  fax: provider?.facility_phone_number || '',
  
  // Home Health Agency Question
  homeHealthAgency: '',
  
  // DME License
  dmeLicense: '',
  
  // Who bills
  whoBills: '',
  
  // Diagnosis - 3 wounds
  wounds: [
    {
      woundNumber: 1,
      icd10Code: '',
      woundLocation: '',
      length: '',
      width: '',
      depth: '',
      depthStatus: 'partial',
      exudateLevel: 'mild',
      woundCausedBySurgical: 'no',
      woundHasUndergoneDebridement: 'no',
    },
    {
      woundNumber: 2,
      icd10Code: '',
      woundLocation: '',
      length: '',
      width: '',
      depth: '',
      depthStatus: 'partial',
      exudateLevel: 'mild',
      woundCausedBySurgical: 'no',
      woundHasUndergoneDebridement: 'no',
    },
    {
      woundNumber: 3,
      icd10Code: '',
      woundLocation: '',
      length: '',
      width: '',
      depth: '',
      depthStatus: 'partial',
      exudateLevel: 'mild',
      woundCausedBySurgical: 'no',
      woundHasUndergoneDebridement: 'no',
    }
  ],
  
  // Standard Written Order
  surgeryDate: '',
  lengthOfNeed: '1',
  selectedKitOption: null,
  
  // Physician Signature
  physicianSignature: provider?.full_name || '',
  signatureDate: new Date().toISOString().split('T')[0],
});

// Helper: Determine allowed kit sizes based on wound #1 length
const getAllowedKitSizes = (woundLength) => {
  const length = parseFloat(woundLength);
  
  if (isNaN(length) || length === 0) {
    return ['small', 'medium', 'large']; // Allow all if no measurement yet
  }
  
  if (length <= 2) {
    return ['small'];
  } else if (length > 2 && length <= 4) {
    return ['medium'];
  } else {
    return ['large'];
  }
};

// ============================================================================
// SUCCESS TOAST
// ============================================================================

const SuccessToast = ({ t, sasUrl, onClose }) => (
  <div className="bg-white p-4 rounded-xl shadow-lg flex flex-col items-start w-80">
    <div className="flex items-center mb-2">
      <Check className="w-5 h-5 text-green-500 mr-2" />
      <span className="font-bold text-gray-800">IVR Form Submitted!</span>
    </div>
    <p className="text-sm text-gray-600 mb-3">
      PDF generated and submitted for verification.
    </p>
    <a
      href={sasUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        toast.dismiss(t.id);
        onClose();
      }}
      className="w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
    >
      View/Download PDF
    </a>
    <button
      onClick={() => {
        toast.dismiss(t.id);
        onClose();
      }}
      className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700"
    >
      Close
    </button>
  </div>
);

// ============================================================================
// INLINE STYLES (PDF-matching form styles)
// ============================================================================

const styles = {
  // Outer form border
  formOuter: {
    border: '3px solid #1a1a1a',
    backgroundColor: '#fff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '11px',
    lineHeight: '1.3',
  },
  // Header area (top of form)
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.2fr 1fr',
    borderBottom: '3px solid #1a1a1a',
  },
  headerLeft: {
    padding: '8px 10px',
    borderRight: '2px solid #1a1a1a',
  },
  headerCenter: {
    padding: '8px 10px',
    textAlign: 'center',
    borderRight: '2px solid #1a1a1a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRight: {
    padding: '8px 10px',
  },
  // Section header bars
  greenBar: {
    backgroundColor: '#2d8c2d',
    color: '#fff',
    fontWeight: '900',
    fontSize: '11px',
    padding: '4px 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderTop: '2px solid #1a1a1a',
    borderBottom: '2px solid #1a1a1a',
  },
  blueBar: {
    backgroundColor: '#2b5ea7',
    color: '#fff',
    fontWeight: '900',
    fontSize: '11px',
    padding: '4px 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderTop: '2px solid #1a1a1a',
    borderBottom: '2px solid #1a1a1a',
  },
  purpleBar: {
    backgroundColor: '#6b3fa0',
    color: '#fff',
    fontWeight: '900',
    fontSize: '11px',
    padding: '4px 8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderTop: '2px solid #1a1a1a',
    borderBottom: '2px solid #1a1a1a',
  },
  // Form rows
  formRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #999',
    minHeight: '28px',
  },
  formCell: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 6px',
    minHeight: '28px',
    borderRight: '1px solid #999',
    gap: '4px',
  },
  formCellLast: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 6px',
    minHeight: '28px',
    gap: '4px',
  },
  label: {
    fontWeight: '700',
    fontSize: '10px',
    whiteSpace: 'nowrap',
    color: '#1a1a1a',
  },
  input: {
    border: 'none',
    borderBottom: '1px solid #333',
    outline: 'none',
    fontSize: '11px',
    padding: '2px 4px',
    flex: 1,
    minWidth: '40px',
    backgroundColor: 'transparent',
  },
  checkbox: {
    width: '13px',
    height: '13px',
    accentColor: '#1a1a1a',
    cursor: 'pointer',
  },
  radio: {
    width: '13px',
    height: '13px',
    accentColor: '#1a1a1a',
    cursor: 'pointer',
  },
  // Diagnosis table
  diagnosisHeader: {
    backgroundColor: '#f0f0f0',
    display: 'grid',
    gridTemplateColumns: '80px 1fr 1fr 1fr 1fr 1fr 1fr',
    borderBottom: '2px solid #1a1a1a',
    fontSize: '9px',
    fontWeight: '700',
  },
  diagnosisCell: {
    padding: '4px 6px',
    borderRight: '1px solid #999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  // SWO grid
  swoGridHeader: {
    fontWeight: '900',
    fontSize: '10px',
    padding: '4px 6px',
    textAlign: 'center',
    color: '#fff',
    textTransform: 'uppercase',
  },
  swoCell: {
    padding: '6px 8px',
    borderRight: '1px solid #999',
    borderBottom: '1px solid #999',
    fontSize: '9px',
    lineHeight: '1.4',
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProPacksIVRFormModal({
  open,
  onClose,
  initialData,
  selectedProviderDetails,
  onFormComplete,
  existingIvrFormId = null,
}) {
  const provider = selectedProviderDetails;
  const [formData, setFormData] = useState(() => getInitialState(initialData, provider));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (open) {
      console.log('🔍 ProPacks IVR Modal Opened - Resetting form');
      setFormData(getInitialState(initialData, provider));
      setIsSubmitting(false);
    }
  }, [open, initialData, provider]);

  // ✅ DEBUG LOGS
  useEffect(() => {
    if (open) {
      console.log('🔍 ProPacks IVR Modal Opened');
      console.log('📋 initialData:', initialData);
      console.log('👨‍⚕️ selectedProviderDetails:', selectedProviderDetails);
      console.log('📝 formData:', formData);
    }
  }, [open]);

  // Pre-fill wound #1 from initialData.woundDetails
  useEffect(() => {
    if (initialData?.woundDetails && open) {
      setFormData(prev => ({
        ...prev,
        wounds: [
          {
            ...prev.wounds[0],
            icd10Code: initialData.woundDetails.icd10 || '',
            woundLocation: initialData.woundDetails.location || '',
            length: initialData.woundDetails.length || '',
            width: initialData.woundDetails.width || '',
            depth: initialData.woundDetails.depth || '',
            exudateLevel: initialData.woundDetails.drainage?.toLowerCase() === 'moderate' ? 'moderate' : 'mild',
          },
          prev.wounds[1],
          prev.wounds[2]
        ]
      }));
    }
  }, [initialData, open]);

  // Re-initialize when provider data becomes available
useEffect(() => {
  if (open && provider) {
    console.log('🔄 Provider data loaded, updating form...');
    setFormData(prev => ({
      ...prev,
      practiceName: provider?.facility || prev.practiceName,
      npi: provider?.npi_number || prev.npi,
      practiceAddress: provider?.address || prev.practiceAddress,
      practiceCity: provider?.city || prev.practiceCity,
      practiceState: provider?.state || prev.practiceState,
      practiceZip: provider?.zip_code || prev.practiceZip,  // ✅ FIXED
      contactName: provider?.full_name || prev.contactName,
      contactEmail: provider?.email || prev.contactEmail,
      phone: provider?.phone_number || prev.phone,
      fax: provider?.facility_phone_number || prev.fax,
      physicianSignature: provider?.full_name || prev.physicianSignature,
    }));
  }
}, [open, provider]);
// Re-initialize patient data when it becomes available
useEffect(() => {
  if (open && initialData) {
    console.log('🔄 Patient data loaded, updating form...');
    setFormData(prev => ({
      ...prev,
      // Patient Info
      patientName: initialData.first_name && initialData.last_name 
        ? `${initialData.first_name} ${initialData.last_name}` 
        : prev.patientName,
      dob: initialData.date_of_birth || prev.dob,
      gender: initialData.gender || prev.gender,
      address: initialData.address || prev.address,
      city: initialData.city || prev.city,
      state: initialData.state || prev.state,
      zip: initialData.zip_code || prev.zip,
      
      // Insurance Info
      primaryInsurance: initialData.primary_insurance || prev.primaryInsurance,
      primaryPolicyNumber: initialData.primary_insurance_number || prev.primaryPolicyNumber,
      secondaryInsurance: initialData.secondary_insurance || prev.secondaryInsurance,
      secondaryPolicyNumber: initialData.secondary_insurance_number || prev.secondaryPolicyNumber,
    }));
  }
}, [open, initialData]);

const buildGroupedKitOptions = () => {
  const grouped = {};
  Object.values(PROPACKS_IVR_GRID_OPTIONS).forEach(option => {
    if (!grouped[option.category]) {
      grouped[option.category] = [];
    }
    grouped[option.category].push({
      id: option.id,
      displayName: option.displayName,
      category: option.category,
      products: option.products.map(p => ({
        code: p.code,
        quantity: p.units,
        description: p.description || '',
      })),
    });
  });
  return grouped;
};



  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  const handleWoundChange = useCallback((woundIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      wounds: prev.wounds.map((wound, idx) => 
        idx === woundIndex ? { ...wound, [field]: value } : wound
      )
    }));
  }, []);

  const handleKitSelection = useCallback((optionId) => {
    setFormData(prev => ({ ...prev, selectedKitOption: optionId }));
  }, []);

  const validateForm = useCallback(() => {
    const errors = [];
    if (!formData.primaryInsurance || formData.primaryInsurance.trim() === '') {
        errors.push("Primary Insurance is required");
    }
    if (!formData.primaryPolicyNumber || formData.primaryPolicyNumber.trim() === '') {
        errors.push("Primary Policy Number is required");
    }
    if (!formData.wounds[0].icd10Code) {
      errors.push("Wound #1 ICD-10 Code is required");
    }
    if (!formData.surgeryDate) {
      errors.push("Discharge/Delivery Date is required");
    }
    if (!formData.selectedKitOption) {
      errors.push("Please select a Kit");
    }
    if (!formData.physicianSignature) {
      errors.push("Physician signature is required");
    }
    return errors;
  }, [formData]);

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      if (formRef.current) {
        formRef.current.scrollTop = 0;
      }
      validationErrors.forEach(err => toast.error(err));
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Generating ProPacks IVR PDF...");

    try {
      const response = await fetch("https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/forms/save-vr/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          patient_id: initialData.id,
          ivr_type: 'propacks',
          form_data: formData,
          grouped_kit_options: buildGroupedKitOptions(),
          ivr_form_id: existingIvrFormId || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.detail || "Failed to submit IVR form");
      }

      const result = await response.json();
      toast.dismiss(toastId);
      toast(
        (t) => <SuccessToast t={t} sasUrl={result.sas_url} onClose={onClose} />,
        { duration: Infinity }
      );

      if (onFormComplete) {
        onFormComplete(result, formData);
      }
    } catch (error) {
      console.error("Error submitting ProPacks IVR form:", error);
      toast.dismiss(toastId);
      toast.error('A new order cannot be placed for this patient within 5 days of a previous submission. Please try again after the waiting period.');

    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-gray-100 rounded-lg shadow-2xl w-full max-w-[900px] my-4">
        
        {/* ================================================================ */}
        {/* MODAL HEADER (sticky)                                            */}
        {/* ================================================================ */}
        <div className="sticky top-0 z-20 bg-blue-900 px-4 py-3 flex justify-between items-center rounded-t-lg">
          <h2 className="text-base font-bold text-white tracking-wide">
            ProPacks Insurance Verification Request
          </h2>
          <motion.button
            onClick={onClose}
            className="text-white hover:text-red-300 transition"
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>

        {/* ================================================================ */}
        {/* FORM BODY                                                        */}
        {/* ================================================================ */}
        <div className="p-3 sm:p-4" ref={formRef}>
          <div style={styles.formOuter}>

            {/* ============================================================ */}
            {/* TOP HEADER: Logo | Title | Physician Info                     */}
            {/* ============================================================ */}
            <div style={styles.headerRow}>
              
              {/* LEFT: Serviced By + Billing */}
              <div style={styles.headerLeft}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <img src={logo} alt="Logo" style={{ width: '36px', height: '36px' }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '900', color: '#1a365d' }}>
                      ProMed<span style={{ color: '#2d8c2d' }}> Health Plus</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', marginTop: '8px' }}>
                  <span style={{ fontWeight: '700', fontSize: '9px' }}>Who will bill for ProPacks?</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.whoBills === 'promed'} onChange={() => setFormData(p => ({...p, whoBills: 'promed'}))} style={styles.checkbox} />
                    <span style={{ fontSize: '9px', fontWeight: '600' }}>ProMed Bill</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.whoBills === 'self'} onChange={() => setFormData(p => ({...p, whoBills: 'self'}))} style={styles.checkbox} />
                    <span style={{ fontSize: '9px', fontWeight: '600' }}>Self Bill</span>
                  </label>
                </div>
              </div>

              {/* CENTER: Title */}
              <div style={styles.headerCenter}>
                <div style={{ fontSize: '24px', fontWeight: '900', color: '#1a1a1a', letterSpacing: '2px', lineHeight: '1.1' }}>
                  ProPacks
                </div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#1a1a1a', marginTop: '2px' }}>
                  Insurance Verification Request
                </div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#1a1a1a' }}>
                  & Standard Written Order
                </div>
                <div style={{ fontSize: '9px', fontWeight: '600', color: '#444', marginTop: '4px', fontStyle: 'italic' }}>
                  Is this patient currently receiving wound care from a home health agency?
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '3px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                    <input type="radio" name="homeHealthAgency" value="yes" checked={formData.homeHealthAgency === 'yes'} onChange={handleChange} style={styles.radio} />
                    <span style={{ fontSize: '10px', fontWeight: '700' }}>YES</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                    <input type="radio" name="homeHealthAgency" value="no" checked={formData.homeHealthAgency === 'no'} onChange={handleChange} style={styles.radio} />
                    <span style={{ fontSize: '10px', fontWeight: '700' }}>NO</span>
                  </label>
                </div>
              </div>

              {/* RIGHT: Physician Info + DME */}
              <div style={styles.headerRight}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '700', fontSize: '10px', whiteSpace: 'nowrap' }}>Physician Name:</span>
                  <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} style={{ ...styles.input, fontSize: '10px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '700', fontSize: '10px', whiteSpace: 'nowrap' }}>Physician NPI#:</span>
                  <input type="text" name="npi" value={formData.npi} onChange={handleChange} style={{ ...styles.input, fontSize: '10px' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0', borderTop: '1px solid #ccc' }}>
                  <span style={{ fontWeight: '700', fontSize: '9px', color: '#c00' }}>Does Provider/Practice have a DME License?</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '4px' }}>
                    <input type="radio" name="dmeLicense" value="yes" checked={formData.dmeLicense === 'yes'} onChange={handleChange} style={styles.radio} />
                    <span style={{ fontSize: '10px', fontWeight: '700', color: '#f59e0b', backgroundColor: '#fef3c7', padding: '0 4px', borderRadius: '2px' }}>YES</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <input type="radio" name="dmeLicense" value="no" checked={formData.dmeLicense === 'no'} onChange={handleChange} style={styles.radio} />
                    <span style={{ fontSize: '10px', fontWeight: '700', color: '#dc2626', backgroundColor: '#fee2e2', padding: '0 4px', borderRadius: '2px' }}>NO</span>
                  </label>
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* PATIENT AND PAYER INFORMATION                                 */}
            {/* ============================================================ */}
            <div style={styles.greenBar}>PATIENT AND PAYER INFORMATION</div>
            
            {/* Row: Patient Name | DOB | Gender */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '2' }}>
                <span style={styles.label}>Patient Name:</span>
                <input type="text" name="patientName" value={formData.patientName} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>DOB:</span>
                <input type="date" name="dob" value={formData.dob} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '0 0 auto', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
                  <input type="radio" name="gender" value="Male" checked={formData.gender === 'Male'} onChange={handleChange} style={styles.checkbox} />
                  <span style={{ ...styles.label }}>Male</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
                  <input type="radio" name="gender" value="Female" checked={formData.gender === 'Female'} onChange={handleChange} style={styles.checkbox} />
                  <span style={{ ...styles.label }}>Female</span>
                </label>
              </div>
            </div>

            {/* Row: Address | City | State | Zip */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '3' }}>
                <span style={{...styles.label, color: '#c00'}}>Address: *</span>
                <input type="text" name="address" value={formData.address} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1.5' }}>
                <span style={{...styles.label, color: '#c00'}}>City: *</span>
                <input type="text" name="city" value={formData.city} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '0.5' }}>
                <span style={{...styles.label, color: '#c00'}}>State: *</span>
                <input type="text" name="state" value={formData.state} onChange={handleChange} style={{ ...styles.input, maxWidth: '40px' }} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1' }}>
                <span style={{...styles.label, color: '#c00'}}>Zip: *</span>
                <input type="text" name="zip" value={formData.zip} onChange={handleChange} style={styles.input} />
              </div>
            </div>

            {/* Row: Primary Insurance | Secondary Insurance */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={{ ...styles.label, color: '#c00' }}>Primary Insurance: *</span>
                <input type="text" name="primaryInsurance" value={formData.primaryInsurance} onChange={handleChange} style={styles.input} placeholder="Required" />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1' }}>
                <span style={styles.label}>Secondary Insurance:</span>
                <input type="text" name="secondaryInsurance" value={formData.secondaryInsurance} onChange={handleChange} style={styles.input} />
              </div>
            </div>

            {/* Row: Policy# | Payer Phone# | Policy# | Payer Phone# */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={{ ...styles.label, color: '#c00' }}>Policy# : *</span>
                <input type="text" name="primaryPolicyNumber" value={formData.primaryPolicyNumber} onChange={handleChange} style={styles.input} placeholder="Required" />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>Payer Phone #:</span>
                <input type="text" name="primaryPayerPhone" value={formData.primaryPayerPhone} onChange={handleChange} style={styles.input} placeholder="Enter" />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>Policy#:</span>
                <input type="text" name="secondaryPolicyNumber" value={formData.secondaryPolicyNumber} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1' }}>
                <span style={styles.label}>Payer Phone #:</span>
                <input type="text" name="secondaryPayerPhone" value={formData.secondaryPayerPhone} onChange={handleChange} style={styles.input} placeholder="Enter" />
              </div>
            </div>

            {/* ============================================================ */}
            {/* PRACTICE INFORMATION                                          */}
            {/* ============================================================ */}
            <div style={styles.blueBar}>PRACTICE INFORMATION</div>

            {/* Row: Practice Name | NPI# | Tax ID# | PTAN# */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '2' }}>
                <span style={styles.label}>Practice Name:</span>
                <input type="text" name="practiceName" value={formData.practiceName} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>NPI#:</span>
                <input type="text" name="npi" value={formData.npi} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>Tax ID#:</span>
                <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} style={styles.input} placeholder="Enter" />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1' }}>
                <span style={styles.label}>PTAN#:</span>
                <input type="text" name="ptanId" value={formData.ptanId} onChange={handleChange} style={styles.input} placeholder="Enter" />
              </div>
            </div>

            {/* Row: Address | City | State | Zip */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '3' }}>
                <span style={{...styles.label, color: '#c00'}}>Address: *</span>
                <input type="text" name="practiceAddress" value={formData.practiceAddress} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1.5' }}>
                <span style={{...styles.label, color: '#c00'}}>City: *</span>
                <input type="text" name="practiceCity" value={formData.practiceCity} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '0.5' }}>
                <span style={{...styles.label, color: '#c00'}}>State: *</span>
                <input type="text" name="practiceState" value={formData.practiceState} onChange={handleChange} style={{ ...styles.input, maxWidth: '40px' }} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1' }}>
                <span style={{...styles.label, color: '#c00'}}>Zip: *</span>
                <input type="text" name="practiceZip" value={formData.practiceZip} onChange={handleChange} style={styles.input} />
              </div>
            </div>

            {/* Row: Contact Name | Contact Email | Phone# | Fax# */}
            <div style={styles.formRow}>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>Contact Name:</span>
                <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={styles.label}>Contact Email:</span>
                <input type="text" name="contactEmail" value={formData.contactEmail} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCell, flex: '0.8' }}>
                <span style={styles.label}>Phone#:</span>
                <input type="text" name="phone" value={formData.phone} onChange={handleChange} style={styles.input} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '0.8' }}>
                <span style={styles.label}>Fax#:</span>
                <input type="text" name="fax" value={formData.fax} onChange={handleChange} style={styles.input} />
              </div>
            </div>

            {/* ============================================================ */}
            {/* PROPACKS DISCLAIMER (italic text from PDF)                    */}
            {/* ============================================================ */}
            <div style={{ padding: '6px 8px', fontSize: '8px', lineHeight: '1.4', color: '#333', borderBottom: '2px solid #1a1a1a', backgroundColor: '#fafafa' }}>
              <span style={{ fontWeight: '700' }}>ProPacks</span> are indicated as first-line wound care for qualifying wounds meeting collagen dressing coverage criteria — including{' '}
              <span style={{ fontWeight: '700', textDecoration: 'underline' }}>post-operative surgical incisions</span> with observed{' '}
              <span style={{ fontWeight: '700', textDecoration: 'underline' }}>drainage</span>{' '}
              <em>(all surgical incisions are full-thickness by nature of the procedure)</em> and{' '}
              <span style={{ fontWeight: '700', textDecoration: 'underline' }}>open wounds</span> such as{' '}
              <span style={{ fontWeight: '700', textDecoration: 'underline' }}>ulcers</span> or{' '}
              <span style={{ fontWeight: '700', textDecoration: 'underline' }}>pressure injuries</span> under active wound management. ProPacks may be ordered{' '}
              <span style={{ color: '#c00', fontWeight: '700' }}>at</span> or{' '}
              <span style={{ color: '#c00', fontWeight: '700' }}>before</span>{' '}
              <span style={{ color: '#c00', fontWeight: '700', textDecoration: 'underline' }}>discharge</span> or at the initial wound evaluation.{' '}
              <span style={{ textDecoration: 'underline' }}>No prior treatment failure or waiting period is required</span>.
            </div>

            {/* ============================================================ */}
            {/* DIAGNOSIS                                                     */}
            {/* ============================================================ */}
            <div style={{ borderBottom: '2px solid #1a1a1a' }}>
              {/* Diagnosis header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', borderBottom: '2px solid #1a1a1a' }}>
                <div style={{ backgroundColor: '#1a365d', color: '#fff', fontWeight: '900', fontSize: '11px', padding: '4px 6px', display: 'flex', alignItems: 'center' }}>
                  DIAGNOSIS
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', borderLeft: '2px solid #1a1a1a' }}>
                  <div style={{ padding: '4px 6px', borderRight: '1px solid #999', fontWeight: '700', fontSize: '10px', display: 'flex', alignItems: 'center' }}>
                    ICD-10 Codes
                  </div>
                  <div style={{ padding: '4px 6px', borderRight: '1px solid #999', fontWeight: '700', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Wound #1:
                  </div>
                  <div style={{ padding: '4px 6px', borderRight: '1px solid #999', fontWeight: '700', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Wound #2:
                  </div>
                  <div style={{ padding: '4px 6px', fontWeight: '700', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Wound #3:
                  </div>
                </div>
              </div>

              {/* Column sub-headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 1fr 1fr 1fr 0.6fr 0.8fr',
                borderBottom: '2px solid #1a1a1a',
                backgroundColor: '#e8e8e8',
                fontSize: '8px',
                fontWeight: '700',
                lineHeight: '1.3',
              }}>
                <div style={{ padding: '3px 4px', borderRight: '1px solid #999', textAlign: 'center' }}>
                  Wound#
                </div>
                <div style={{ padding: '3px 4px', borderRight: '1px solid #999', textAlign: 'center' }}>
                  Wound Caused by or Treated by a Surgical Procedure
                  <div style={{ fontWeight: '400', fontSize: '7px', fontStyle: 'italic', color: '#555' }}>
                    Surgical wound with drainage or disruption, wound healing by secondary intention, I&D, graft/donor site, or any surgically treated wound
                  </div>
                </div>
                <div style={{ padding: '3px 4px', borderRight: '1px solid #999', textAlign: 'center' }}>
                  Wound That Has Undergone Debridement
                  <div style={{ fontWeight: '400', fontSize: '7px', fontStyle: 'italic', color: '#555' }}>
                    Ulcer, pressure injury, open wound, or any wound debrided by any method (sharp, enzymatic, mechanical, or autolytic)
                  </div>
                </div>
                <div style={{ padding: '3px 4px', borderRight: '1px solid #999', textAlign: 'center' }}>
                  L×W×D (cm or in); or, Surgical Incision Length (in)
                </div>
                <div style={{ padding: '3px 4px', borderRight: '1px solid #999', textAlign: 'center' }}>
                  Depth = Full Thickness
                </div>
                <div style={{ padding: '3px 4px', textAlign: 'center' }}>
                  Exudate Level
                </div>
              </div>

              {/* Wound rows */}
              {formData.wounds.map((wound, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 1fr 1fr 1fr 0.6fr 0.8fr',
                  borderBottom: index < 2 ? '1px solid #999' : 'none',
                  minHeight: '36px',
                }}>
                  {/* Wound # */}
                  <div style={{ ...styles.diagnosisCell, fontWeight: '900', fontSize: '13px', borderRight: '1px solid #999' }}>
                    {index + 1}
                  </div>
                  {/* Surgical Procedure Yes/No */}
                  <div style={{ ...styles.diagnosisCell, borderRight: '1px solid #999', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.woundCausedBySurgical === 'yes'} onChange={() => handleWoundChange(index, 'woundCausedBySurgical', 'yes')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>Yes</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.woundCausedBySurgical === 'no'} onChange={() => handleWoundChange(index, 'woundCausedBySurgical', 'no')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>No</span>
                    </label>
                  </div>
                  {/* Debridement Yes/No */}
                  <div style={{ ...styles.diagnosisCell, borderRight: '1px solid #999', gap: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.woundHasUndergoneDebridement === 'yes'} onChange={() => handleWoundChange(index, 'woundHasUndergoneDebridement', 'yes')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>Yes</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.woundHasUndergoneDebridement === 'no'} onChange={() => handleWoundChange(index, 'woundHasUndergoneDebridement', 'no')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>No</span>
                    </label>
                  </div>
                  {/* L x W x D */}
                  <div style={{ ...styles.diagnosisCell, borderRight: '1px solid #999', gap: '2px' }}>
                    <input type="text" value={wound.length} onChange={(e) => handleWoundChange(index, 'length', e.target.value)} placeholder="L" style={{ ...styles.input, maxWidth: '30px', textAlign: 'center', fontSize: '10px' }} />
                    <span style={{ fontSize: '10px' }}>×</span>
                    <input type="text" value={wound.width} onChange={(e) => handleWoundChange(index, 'width', e.target.value)} placeholder="W" style={{ ...styles.input, maxWidth: '30px', textAlign: 'center', fontSize: '10px' }} />
                    <span style={{ fontSize: '10px' }}>×</span>
                    <input type="text" value={wound.depth} onChange={(e) => handleWoundChange(index, 'depth', e.target.value)} placeholder="D" style={{ ...styles.input, maxWidth: '30px', textAlign: 'center', fontSize: '10px' }} />
                  </div>
                  {/* Depth = Full Thickness Yes/No */}
                  <div style={{ ...styles.diagnosisCell, borderRight: '1px solid #999', gap: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.depthStatus === 'full'} onChange={() => handleWoundChange(index, 'depthStatus', 'full')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>Yes</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.depthStatus === 'partial'} onChange={() => handleWoundChange(index, 'depthStatus', 'partial')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>No</span>
                    </label>
                  </div>
                  {/* Exudate Level */}
                  <div style={{ ...styles.diagnosisCell, gap: '6px', borderRight: 'none' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.exudateLevel === 'mild'} onChange={() => handleWoundChange(index, 'exudateLevel', 'mild')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>Mild</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input type="checkbox" checked={wound.exudateLevel === 'moderate'} onChange={() => handleWoundChange(index, 'exudateLevel', 'moderate')} style={styles.checkbox} />
                      <span style={{ fontSize: '9px' }}>Moderate</span>
                    </label>
                  </div>
                </div>
              ))}

              {/* ICD-10 & Wound Location selectors below the table */}
              <div style={{ borderTop: '2px solid #1a1a1a', padding: '8px' }}>
                {formData.wounds.map((wound, index) => (
                  <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: index < 2 ? '6px' : '0' }}>
                    <span style={{ fontWeight: '700', fontSize: '10px', minWidth: '62px' }}>Wound #{index + 1}:</span>
                    <div style={{ flex: '1' }}>
                      <select
                        value={wound.icd10Code}
                        onChange={(e) => handleWoundChange(index, 'icd10Code', e.target.value)}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '3px 4px', fontSize: '10px', backgroundColor: '#fff' }}
                      >
                        <option value="">Select ICD-10 Code {index === 0 ? '*' : ''}</option>
                        {icd10Codes.map(code => (
                          <option key={code.code} value={code.code}>
                            {code.code} - {code.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: '0.6' }}>
                      <input
                        type="text"
                        value={wound.woundLocation}
                        onChange={(e) => handleWoundChange(index, 'woundLocation', e.target.value)}
                        placeholder="Wound Location"
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', padding: '3px 4px', fontSize: '10px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ============================================================ */}
            {/* STANDARD WRITTEN ORDER                                        */}
            {/* ============================================================ */}
            <div style={styles.purpleBar}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>STANDARD WRITTEN ORDER</span>
              </div>
            </div>

            {/* Discharge Date + Length of Need */}
            <div style={{ ...styles.formRow, borderBottom: '2px solid #1a1a1a' }}>
              <div style={{ ...styles.formCell, flex: '1' }}>
                <span style={{ ...styles.label, color: '#c00' }}>Discharge Date / Desired Delivery Date: *</span>
                <input type="date" name="surgeryDate" value={formData.surgeryDate} onChange={handleChange} style={{ ...styles.input, maxWidth: '160px' }} />
              </div>
              <div style={{ ...styles.formCellLast, flex: '1', gap: '10px' }}>
                <span style={styles.label}>Length of Need:</span>
                {['1', '2', '3'].map(month => (
                  <label key={month} style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
                    <input type="radio" name="lengthOfNeed" value={month} checked={formData.lengthOfNeed === month} onChange={handleChange} style={styles.radio} />
                    <span style={{ fontSize: '10px', fontWeight: '600' }}>{month} month{month !== '1' ? 's' : ''}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Kit Selection Grid (3-column layout like PDF) */}
            {/* Kit Selection Grid (3-column layout like PDF) */}
            {/* <div style={{ borderBottom: '2px solid #1a1a1a' }}>
              {formData.wounds[0].length && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  padding: '6px 10px',
                  fontSize: '9px',
                  fontWeight: '700',
                  color: '#92400e',
                  borderBottom: '2px solid #f59e0b',
                }}>
                  ⚠️ Kit size restricted based on Wound #1 length: {formData.wounds[0].length}cm 
                  {parseFloat(formData.wounds[0].length) <= 2 && ' (Small kits only)'}
                  {parseFloat(formData.wounds[0].length) > 2 && parseFloat(formData.wounds[0].length) <= 4 && ' (Medium kits only)'}
                  {parseFloat(formData.wounds[0].length) > 4 && ' (Large kits only)'}
                </div>
              )}
              {(() => {
                const grouped = Object.values(PROPACKS_IVR_GRID_OPTIONS).reduce((acc, option) => {
                  if (!acc[option.category]) acc[option.category] = [];
                  acc[option.category].push(option);
                  return acc;
                }, {});

                const allowedSizes = getAllowedKitSizes(formData.wounds[0].length);

                return Object.entries(grouped).map(([category, options], catIdx) => {
                  const bgColor = catIdx % 2 === 0 ? '#f8f9fa' : '#fff';
                  
                  // Filter options by allowed sizes
                  const filteredOptions = options.filter(opt => allowedSizes.includes(opt.size));
                  
                  // Skip category if no valid options
                  if (filteredOptions.length === 0) return null;
                  
                  return (
                    <div key={category}>
                      <div style={{
                        backgroundColor: '#1e3a5f',
                        color: '#fff',
                        fontWeight: '900',
                        fontSize: '10px',
                        padding: '5px 8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '1px solid #1a1a1a',
                        borderTop: catIdx > 0 ? '2px solid #1a1a1a' : 'none',
                      }}>
                        {category}
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        backgroundColor: bgColor,
                      }}>
                        {filteredOptions.map((option, optIdx) => (
                          <label
                            key={option.id}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '5px',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderRight: optIdx < filteredOptions.length - 1 ? '1px solid #ccc' : 'none',
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: formData.selectedKitOption === option.id ? '#dbeafe' : 'transparent',
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <input
                              type="radio"
                              name="selectedKitOption"
                              value={option.id}
                              checked={formData.selectedKitOption === option.id}
                              onChange={() => handleKitSelection(option.id)}
                              style={{ ...styles.radio, marginTop: '1px', flexShrink: 0 }}
                            />
                            <div>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a1a1a', lineHeight: '1.3' }}>
                                {option.displayName}
                              </div>
                              <div style={{ fontSize: '8px', color: '#555', lineHeight: '1.3', marginTop: '2px' }}>
                                {option.products.map(p => `${p.code} ${p.description} (${p.units})`).join(', ')}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                }).filter(Boolean);
              })()}
            </div> */}
            <div style={{ borderBottom: '2px solid #1a1a1a' }}>
              {/* Duration column headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                backgroundColor: '#e8f4e8',
                borderBottom: '1px solid #1a1a1a',
                fontSize: '9px',
                fontWeight: '700',
                textAlign: 'center',
              }}>
                <div style={{ padding: '4px 6px', borderRight: '1px solid #999', color: '#1a3a1a' }}>
                  📅 30-Day Supply
                </div>
                <div style={{ padding: '4px 6px', borderRight: '1px solid #999', color: '#1a3a1a' }}>
                  📅 15-Day Supply
                </div>
                <div style={{ padding: '4px 6px', color: '#1a3a1a' }}>
                  📅 12-Day Supply
                </div>
              </div>

              {/* Show size restriction warning if wound length is entered */}
              {formData.wounds[0].length && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  padding: '6px 10px',
                  fontSize: '9px',
                  fontWeight: '700',
                  color: '#92400e',
                  borderBottom: '2px solid #f59e0b',
                }}>
                  ⚠️ Kit size restricted based on Wound #1 length: {formData.wounds[0].length}cm
                  {parseFloat(formData.wounds[0].length) <= 2 && ' (Small kits only)'}
                  {parseFloat(formData.wounds[0].length) > 2 && parseFloat(formData.wounds[0].length) <= 4 && ' (Medium kits only)'}
                  {parseFloat(formData.wounds[0].length) > 4 && ' (Large kits only)'}
                </div>
              )}

              {/* Group options by category then render */}
              {(() => {
                const grouped = Object.values(PROPACKS_IVR_GRID_OPTIONS).reduce((acc, option) => {
                  if (!acc[option.category]) acc[option.category] = [];
                  acc[option.category].push(option);
                  return acc;
                }, {});

                const allowedSizes = getAllowedKitSizes(formData.wounds[0].length);

                return Object.entries(grouped).map(([category, options], catIdx) => {
                  const bgColor = catIdx % 2 === 0 ? '#f8f9fa' : '#fff';

                  // Filter options by allowed sizes
                  const filteredOptions = options.filter(opt => allowedSizes.includes(opt.size));

                  // Skip category if no valid options
                  if (filteredOptions.length === 0) return null;

                  // Sort within category: 30-day first, then 15-day, then 12-day
                  const sortedOptions = filteredOptions.sort((a, b) => {
                    const order = { '30-day': 0, '15-day': 1, '12-day': 2 };
                    return (order[a.duration] ?? 9) - (order[b.duration] ?? 9);
                  });

                  return (
                    <div key={category}>
                      {/* Category header */}
                      <div style={{
                        backgroundColor: '#1e3a5f',
                        color: '#fff',
                        fontWeight: '900',
                        fontSize: '10px',
                        padding: '5px 8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderBottom: '1px solid #1a1a1a',
                        borderTop: catIdx > 0 ? '2px solid #1a1a1a' : 'none',
                      }}>
                        {category}
                      </div>

                      {/* Options — always 3 columns (30-day | 15-day | 12-day) */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        backgroundColor: bgColor,
                      }}>
                        {sortedOptions.map((option, optIdx) => (
                          <label
                            key={option.id}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '5px',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              borderRight: optIdx < sortedOptions.length - 1 ? '1px solid #ccc' : 'none',
                              borderBottom: '1px solid #e5e7eb',
                              // Subtle background tint per duration
                              backgroundColor: formData.selectedKitOption === option.id
                                ? '#dbeafe'
                                : option.duration === '15-day'
                                ? (catIdx % 2 === 0 ? '#f0fdf4' : '#f7fef7')  // light green tint for 15-day
                                : 'transparent',
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <input
                              type="radio"
                              name="selectedKitOption"
                              value={option.id}
                              checked={formData.selectedKitOption === option.id}
                              onChange={() => handleKitSelection(option.id)}
                              style={{ ...styles.radio, marginTop: '1px', flexShrink: 0 }}
                            />
                            <div>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#1a1a1a', lineHeight: '1.3' }}>
                                {option.displayName}
                              </div>
                              <div style={{ fontSize: '8px', color: '#555', lineHeight: '1.3', marginTop: '2px' }}>
                                {option.products.map(p => `${p.code} ×${p.units} — ${p.description}`).join(', ')}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                }).filter(Boolean);
              })()}
            </div>


            {/* ============================================================ */}
            {/* PHYSICIAN / PROVIDER SIGNATURE                                */}
            {/* ============================================================ */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 10px',
              gap: '12px',
              backgroundColor: '#fffbeb',
              borderTop: '2px solid #1a1a1a',
            }}>
              <span style={{ fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                Physician / Provider Signature:
              </span>
              <input
                type="text"
                name="physicianSignature"
                value={formData.physicianSignature}
                onChange={handleChange}
                style={{
                  border: 'none',
                  borderBottom: '2px solid #333',
                  outline: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  padding: '2px 4px',
                  flex: 1,
                  backgroundColor: 'transparent',
                  fontStyle: 'italic',
                }}
              />
              <span style={{ fontWeight: '900', fontSize: '10px', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                Date:
              </span>
              <input
                type="date"
                name="signatureDate"
                value={formData.signatureDate}
                onChange={handleChange}
                style={{
                  border: 'none',
                  borderBottom: '2px solid #333',
                  outline: 'none',
                  fontSize: '11px',
                  padding: '2px 4px',
                  width: '140px',
                  backgroundColor: 'transparent',
                }}
              />
            </div>

          </div>{/* end formOuter */}

          {/* ============================================================ */}
          {/* ACTION BUTTONS                                                */}
          {/* ============================================================ */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors disabled:opacity-50 font-semibold text-sm flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Submit ProPacks IVR Form
                </>
              )}
            </button>
          </div>

        </div>{/* end form body */}
      </div>{/* end modal */}
    </div>
  );
}